import { useCallback, useMemo, useRef, useState } from "react";
import GuidedTour from "../UI/GuidedTour";
import classes from "../UI/GuidedTour.module.css";

// Tour state is local. Opening/closing never dispatches settings or touches audio.
export default function FretboardQuickTour({ training }) {
  const [active, setActive] = useState(false);
  const scroll = useRef(null);
  const close = useCallback(() => {
    setActive(false);
    if (scroll.current?.element.isConnected) {
      scroll.current.element.scrollTop = scroll.current.top;
      scroll.current.element.scrollLeft = scroll.current.left;
      window.scrollTo({ top: scroll.current.y, left: scroll.current.x, behavior: "instant" });
    }
  }, []);
  const steps = useMemo(() => [
    {
      target: "fretboard-notes", title: "Choose your instrument and notes",
      text: "Choose your instrument and the notes you'd like to practise. Use a preset scale or select individual note names to concentrate on. Your selections stay unchanged during this tour.",
    },
    {
      target: "fretboard-options", title: "Adjust the challenge",
      text: "Choose sharps, flats or both. Sound is optional: turn it Off for silent practice. Display note names while learning, then hide them to test your memory. In Use Both, the current question determines the diagram's accidental spelling.",
    },
    {
      target: "fretboard-start", title: "Start practising",
      text: training
        ? "Training is already active. Find each requested note on the fretboard. Correct answers play your selected note when Sound is On and advance after 300 milliseconds. Stop Training returns to exploration; this tour leaves the exercise running."
        : "Start Training asks you for note names. Find each requested note on the fretboard. Correct answers play your selected note when Sound is On and advance after 300 milliseconds. This tour does not start training.",
    },
    {
      target: "fretboard-neck", title: "Find the note on the fretboard",
      getTarget: () => document.querySelector('[data-tour-target="fretboard-neck"] img'),
      text: training
        ? "Select any string and fret producing the requested note, including open strings. Any matching octave is correct. Green means correct; red means try again immediately. You can answer after closing the tour."
        : "Here, exploration clicks play notes when Sound is On. During training, select any string and fret producing the requested note, including open strings. Any matching octave is correct. Green advances automatically; red lets you retry immediately.",
    },
  ].map(step => ({ ...step, unavailableText: "This part of the exercise is currently unavailable. Continue or exit the tour; your settings have not been changed." })), [training]);

  return <>
    <button type="button" className={classes.start} onClick={event => {
      const element = event.currentTarget.closest('[data-fretboard-trainer-page]');
      scroll.current = element ? { element, top: element.scrollTop, left: element.scrollLeft,
        x: window.scrollX, y: window.scrollY } : null;
      setActive(true);
    }}>Take a quick tour</button>
    {active && <GuidedTour steps={steps} onClose={close} />}
  </>;
}
